import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faEye,
    faEyeSlash,
    faMobileAlt,
    faMoneyBillWave,
    faSearch,
    faSpinner,
    faBuilding,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthGuard } from "../App";
import { EmailGuard } from "../App";
import { Oval } from "react-loader-spinner";
import { StandardURL } from "../App";

// Helper function for currency formatting (assuming KES)
const formatCurrency = (amount) => {
    const numericAmount = parseFloat(amount) || 0;
    return new Intl.NumberFormat('en-KE', { 
        style: 'currency', 
        currency: 'KES', 
        minimumFractionDigits: 2,
    }).format(numericAmount).replace('KES', 'Ksh ');
};

const WithdrawalsLandlordAdminPage = () => {
    const [balance, setBalance] = useState(null);
    const [showBalance, setShowBalance] = useState(true);
    const [withdrawalAmount, setWithdrawalAmount] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [selectedChannel, setSelectedChannel] = useState(null);
    const [selectedMode, setSelectedMode] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isBalanceLoading, setIsBalanceLoading] = useState(false); // New state for balance fetch
    const [propertyId, setPropertyId] = useState("");

    const token = localStorage.getItem("token");

    const fetchBalance = useCallback(async () => {
        if (!token || !propertyId) return;

        setIsBalanceLoading(true);
        setBalance(null); // Clear old balance

        try {
            const response = await axios.get(
                `${StandardURL}/iNyumba/balance/owner/${propertyId}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (response.data && response.data.response.length > 0) {
                setBalance(response.data.response[0].balance);
                toast.success(`Balance fetched for Property ID: ${propertyId}`);
            } else {
                toast.info(`No balance record found for Property ID: ${propertyId}.`);
            }
        } catch (error) {
            console.error("Error fetching balance:", error);
            const errorMessage = error.response?.data?.response || "Failed to fetch balance.";
            toast.error(errorMessage);
        } finally {
            setIsBalanceLoading(false);
        }
    }, [token, propertyId]);

    // Handle initial fetch or refetch when Property ID is searched
    // Note: The original implementation was triggering fetch on any propertyId change, 
    // I'm modifying it to be triggered by the user button press for better control, 
    // but the `useEffect` for propertyId change is kept simple here.
    // The main fetch logic is moved to `handleFetchBalanceClick`.

    const handleFetchBalanceClick = () => {
        if (!propertyId) {
            toast.warn("Please enter a Property ID first.");
            return;
        }
        fetchBalance();
    };

    const handleWithdrawalSubmit = async (e) => {
        e.preventDefault();

        if (!token || !withdrawalAmount || !selectedMode || !propertyId) {
            toast.error("Please fill in all required fields (Property ID, Amount, Mode).");
            return;
        }
        if (selectedMode === "mobilemoney" && !phoneNumber) {
             toast.error("Phone number and channel selection are required for Mobile Money.");
            return;
        }
        if (parseFloat(withdrawalAmount) > parseFloat(balance)) {
             toast.error("Withdrawal amount exceeds available balance.");
            return;
        }


        setIsLoading(true);

        try {
            let response;
            
            // NOTE: The original code only handles 'mobilemoney' logic, so we keep that structure.

            if (selectedMode === "mobilemoney") {
                const requestData = {
                    amount: withdrawalAmount,
                    phone_number: phoneNumber,
                    channel: selectedChannel,
                    propertyId: propertyId,
                };

                // The original code uses JSON.stringify and sets Content-Type, which is retained.
                const jsonData = JSON.stringify(requestData);
                response = await axios.post(
                    `${StandardURL}/iNyumba/landlordsettlementlinkinitiated`,
                    jsonData,
                    {
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                // Handling responses based on the original logic
                if (response.data.responseCode === 200) {
                    const message = response.data.response;
                    if (message.includes("Withdrawal has been blocked")) {
                        toast.info("Withdrawal has been blocked. Kindly contact admin.");
                    } else if (message.includes("check their email for the withdrawal link")) {
                        toast.success("Withdrawal initiated. Merchant should check their email for the withdrawal link.");
                    } else if (message.includes("You have insufficient funds")) {
                        toast.error(message);
                    } else {
                         toast.success("Withdrawal process started successfully.");
                    }
                } else if (response.data.responseCode === 201) {
                    // This covers the original else if block where responseCode is 201
                    toast.error(response.data.response);
                } else {
                    // Fallback for other non-200, non-201 successful responses (if any)
                    toast.error(response.data.response || "An unexpected error occurred during initiation.");
                }
            }
        } catch (error) {
            const errorMessage = error.response?.data?.response || "Error submitting withdrawal. Please check details.";
            toast.error(errorMessage);
            console.error("Error submitting withdrawal:", error);
        } finally {
            setIsLoading(false);
            // Optionally clear form fields on success/failure
            setWithdrawalAmount("");
            // Refresh the balance after withdrawal attempt
            fetchBalance(); 
        }
    };

    const toggleShowBalance = () => {
        setShowBalance(!showBalance);
    };

    const handleModeSelection = (mode) => {
        setSelectedMode(mode);
        // Reset channel when mode changes if needed, but in this case, only 'mobilemoney' is an option
        if (mode !== "mobilemoney") {
            setSelectedChannel(null);
            setPhoneNumber("");
        }
    };

    const handleMobileMoneyClick = (channel) => {
        setSelectedChannel(channel);
    };

    const balanceColor = balance > 0 ? 'text-green-600' : 'text-gray-500';

    return (
        <EmailGuard>
            <AuthGuard>
                <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
                    <div className="max-w-xl mx-auto bg-white shadow-2xl rounded-xl p-6 lg:p-8">
                        
                        <h1 className="text-3xl font-bold text-gray-900 mb-6 flex items-center border-b pb-4">
                            <FontAwesomeIcon icon={faMoneyBillWave} className="text-red-500 mr-3" /> Landlord Fund Withdrawal
                        </h1>

                        {/* Property ID and Balance Fetch */}
                        <div className="mb-8 p-4 bg-gray-50 rounded-lg shadow-inner">
                            <label className="block mb-2 font-semibold text-gray-700 flex items-center">
                                <FontAwesomeIcon icon={faBuilding} className="mr-2 text-red-500" /> Landlord Property ID
                            </label>
                            <div className="flex space-x-2">
                                <input
                                    type="text"
                                    value={propertyId}
                                    onChange={(e) => setPropertyId(e.target.value)}
                                    className="w-full border border-gray-300 rounded-md p-2 focus:ring-red-500 focus:border-red-500 transition"
                                    placeholder="Enter Property ID (e.g., PROP-001)"
                                />
                                <button
                                    onClick={handleFetchBalanceClick}
                                    disabled={isBalanceLoading || !propertyId}
                                    className={`flex items-center justify-center py-2 px-4 rounded-md font-semibold transition ${
                                        isBalanceLoading 
                                            ? "bg-gray-400 cursor-not-allowed" 
                                            : "bg-red-500 hover:bg-red-600 text-white"
                                    }`}
                                >
                                    {isBalanceLoading ? (
                                        <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                                    ) : (
                                        <FontAwesomeIcon icon={faSearch} className="mr-2" />
                                    )}
                                    Fetch
                                </button>
                            </div>
                        </div>

                        {/* Balance Display Card */}
                        <div className={`p-5 mb-6 rounded-lg border-l-4 ${balance ? 'border-red-500 bg-red-50 shadow-md' : 'border-gray-300 bg-gray-100'}`}>
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-600">Available Balance for Property ID: **{propertyId || '---'}**</span>
                                <button
                                    onClick={toggleShowBalance}
                                    className="ml-4 p-1 rounded-full hover:bg-red-100 transition focus:outline-none"
                                >
                                    <FontAwesomeIcon
                                        icon={showBalance ? faEyeSlash : faEye}
                                        className="text-gray-500 w-5 h-5"
                                    />
                                </button>
                            </div>
                            <span className={`font-extrabold text-3xl ${showBalance ? balanceColor : 'text-gray-900'}`}>
                                {showBalance ? (balance !== null ? formatCurrency(balance) : "N/A") : "Ksh XXXXXX.XX"}
                            </span>
                             {balance !== null && balance < 0 && (
                                <p className="text-sm text-red-600 mt-2">Note: Balance is negative, withdrawal is not possible.</p>
                            )}
                        </div>

                        <form onSubmit={handleWithdrawalSubmit}>
                            {/* Withdrawal Mode Selection (Simplified as only one mode exists) */}
                            <div className="mb-4">
                                <label className="block mb-2 font-semibold text-gray-700">Select Withdrawal Mode</label>
                                <div className="flex space-x-4">
                                    <button
                                        type="button"
                                        className={`flex items-center p-3 rounded-lg border-2 transition duration-150 ${
                                            selectedMode === "mobilemoney"
                                                ? "border-red-500 bg-red-100 text-red-700 shadow-md"
                                                : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
                                        }`}
                                        onClick={() => handleModeSelection("mobilemoney")}
                                    >
                                        <FontAwesomeIcon icon={faMobileAlt} className="mr-3 w-5 h-5" />
                                        <span className="font-bold">Mobile Money</span>
                                    </button>
                                </div>
                            </div>

                            {/* Mobile Money Options (Always shown since it's the only mode) */}
                            {selectedMode === "mobilemoney" && (
                                <div className="mb-6 p-4 border border-red-200 rounded-lg bg-red-50">
                                    <p className="font-semibold text-red-700 mb-3">Mobile Money Channel</p>
                                    <div className="flex space-x-4 mb-4">
                                        {/* M-Pesa Button */}
                                        <button
                                            type="button"
                                            className={`py-2 px-4 rounded-md font-semibold border-2 transition ${
                                                selectedChannel === "mpesa"
                                                    ? "bg-white border-green-500 text-green-600 shadow-sm"
                                                    : "bg-white border-gray-300 text-gray-600 hover:bg-gray-100"
                                            }`}
                                            onClick={() => handleMobileMoneyClick("mpesa")}
                                        >
                                            M-Pesa
                                        </button>
                                        {/* Airtel Money Button */}
                                        <button
                                            type="button"
                                            className={`py-2 px-4 rounded-md font-semibold border-2 transition ${
                                                selectedChannel === "airtelmoney"
                                                    ? "bg-white border-red-500 text-red-600 shadow-sm"
                                                    : "bg-white border-gray-300 text-gray-600 hover:bg-gray-100"
                                            }`}
                                            onClick={() => handleMobileMoneyClick("airtelmoney")}
                                        >
                                            Airtel Money
                                        </button>
                                    </div>

                                    {/* Amount Input */}
                                    <label className="block mb-2 font-semibold text-red-700">Amount to Withdraw</label>
                                    <input
                                        type="number"
                                        value={withdrawalAmount}
                                        onChange={(e) => setWithdrawalAmount(e.target.value)}
                                        className="w-full border border-gray-300 rounded-md p-2 mb-4 focus:ring-red-500 focus:border-red-500"
                                        placeholder="Enter amount"
                                        min="1"
                                    />
                                    
                                    {/* Phone Number Input */}
                                    <label className="block mb-2 font-semibold text-red-700">Recipient Phone Number</label>
                                    <input
                                        type="tel"
                                        value={phoneNumber}
                                        onChange={(e) => setPhoneNumber(e.target.value)}
                                        className="w-full border border-gray-300 rounded-md p-2 mb-4 focus:ring-red-500 focus:border-red-500"
                                        placeholder="e.g., 2547XXXXXXXX"
                                    />
                                </div>
                            )}

                            {/* Submit Button */}
                            <button
                                type="submit"
                                disabled={isLoading || !selectedChannel || !withdrawalAmount || !phoneNumber || !balance || parseFloat(balance) <= 0}
                                className={`w-full flex items-center justify-center py-3 px-4 rounded-md font-bold transition duration-300 ease-in-out ${
                                    isLoading || !selectedChannel || !withdrawalAmount || !phoneNumber || !balance || parseFloat(balance) <= 0
                                        ? "bg-gray-400 cursor-not-allowed"
                                        : "bg-green-600 hover:bg-green-700 text-white shadow-lg"
                                }`}
                            >
                                {isLoading ? (
                                    <>
                                        <Oval height={20} width={20} color="white" secondaryColor="white" visible={true} ariaLabel="loading-indicator" />
                                        <span className="ml-2">Processing Withdrawal...</span>
                                    </>
                                ) : (
                                    "Submit Withdrawal"
                                )}
                            </button>
                        </form>
                    </div>
                </div>
            </AuthGuard>
        </EmailGuard>
    );
};

export default WithdrawalsLandlordAdminPage;